import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { Edit2, Trash2 } from 'react-feather'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import {
  URL_CONFIGURATIONS,
  URL_CONFIGURATIONS_ADD,
  URL_WORKPLACE,
} from 'utils/pathUrl'
import {
  useAddConfigurationMutation,
  useFetchConfigurationsQuery,
} from './configurationsListApiSlice'
import ModalAddConfiguration from './ModalAddConfiguration'

const ListConfigurations = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [isOpenModalAddConfiguration, setIsOpenModalAddConfiguration] =
    useState(false)

  const { data, isLoading, refetch, isFetching } = useFetchConfigurationsQuery()

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [searchInput, setSearchInput] = useState('')
  const [dataConfigurationById, setDataConfigurationById] = useState(null)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isFilter, setIsFilter] = useState(false)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [addConfiguration, { isLoading: isRemove }] =
    useAddConfigurationMutation()

  const filteredData = useMemo(() => {
    const flattenedData = (data || []).flatMap((item) => {
      return item.commodities.map((commodityItem, index) => {
        return {
          id: commodityItem.id,
          originalId: item.id,
          type: item.type || '',
          commodity: String(
            commodityItem.commodity?.label || commodityItem.commodity || '',
          ),
          psdh: commodityItem.psdh,
          unit: String(commodityItem.unit?.label || commodityItem.unit || ''),
        }
      })
    })

    if (!searchInput.trim()) {
      setIsFilter(false)
      return flattenedData
    }

    setIsFilter(true)
    const searchTerm = searchInput.toLowerCase().trim()

    return flattenedData.filter((item) => {
      const type = item.type.toLowerCase()
      const commodity = item.commodity.toLowerCase()
      const psdh = String(item.psdh).toLowerCase()
      const unit = item.unit.toLowerCase()

      return (
        type.includes(searchTerm) ||
        commodity.includes(searchTerm) ||
        psdh.includes(searchTerm) ||
        unit.includes(searchTerm)
      )
    })
  }, [searchInput, data])

  // TOGGLE MODAL ADD CONFIGURATION
  const toggleModalAddConfiguration = () => {
    setIsOpenModalAddConfiguration((prev) => !prev)
    setDataConfigurationById(null)
  }

  // TOGGLE MODAL ADD CONFIGURATION
  const navigateToConfigurationAdd = () => {
    navigate(URL_CONFIGURATIONS_ADD)
  }

  // NEW COLUMNS
  const columns = [
    {
      name: <span>Jenis</span>,
      selector: 'type',
      grow: 2,
      cell: (row) => {
        return <span>{row?.type ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Barang</span>,
      selector: 'commodity',
      grow: 2,
      left: true,
      minWidth: '200px',
      cell: (row) => {
        return (
          <span data-testid={`configurations-row-${row.id}`}>
            {row?.commodity ?? '-'}
          </span>
        )
      },
    },
    {
      name: <span>PSDH</span>,
      selector: 'psdh',
      grow: 2,
      right: true,
      cell: (row) => {
        return <span>Rp{newFormatThousandSeparator(row?.psdh ?? 0)}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Satuan</span>,
      selector: 'unit',
      grow: 2,
      cell: (row) => {
        return <span>{row?.unit ?? '-'}</span>
      },
      minWidth: '60px',
    },
    {
      name: '',
      width: '150px',
      cell: (row) => {
        return (
          <div
            className="d-flex align-items-center"
            style={{ gap: '16px', flexGrow: 1 }}
          >
            <div id={`edit-${row?.id}`}>
              <Edit2
                size={18}
                onClick={() => {
                  toggleModalAddConfiguration()
                  setDataConfigurationById(row)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`configurations-edit-${row?.id}`}
              />
            </div>
            <PMBTooltip target={`edit-${row?.id}`} text="Ubah" />

            <div id={`remove-${row?.id}`}>
              <Trash2
                color="red"
                size={18}
                onClick={() => {
                  setIsOpenDeleteModal(true)
                  setDataConfigurationById(row)
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`configurations-delete-${row?.id}`}
              />
            </div>
            <PMBTooltip target={`remove-${row?.id}`} text="Hapus" />
          </div>
        )
      },
    },
  ]

  // HANDLE SUBMIT REMOVE BY COMMODITY ID
  const handleSubmitRemoveById = async (id) => {
    try {
      if (!dataConfigurationById) {
        enqueueSnackbar('Tidak ada data yang dipilih untuk dihapus', {
          variant: 'error',
        })
        return
      }

      const findDataByOriginalId = data?.filter(
        (item) => item.id === dataConfigurationById?.originalId,
      )

      const filterDataNotWithCommodityId = data?.map((item) => {
        return {
          ...item,
          commodities: item.commodities.filter(
            (commodity) => commodity.id !== id,
          ),
        }
      })

      const filterDataNotWithTypeId = data?.filter(
        (item) => item.id !== dataConfigurationById?.originalId,
      )

      const response = await addConfiguration(
        findDataByOriginalId?.[0]?.commodities?.length === 1
          ? filterDataNotWithTypeId
          : filterDataNotWithCommodityId,
      )
      if (response && response?.data) {
        enqueueSnackbar(
          `Barang ${dataConfigurationById?.commodity} berhasil dihapus dari jenis ${dataConfigurationById?.type}`,
          {
            variant: 'success',
          },
        )
        setIsOpenDeleteModal(false)
        setDataConfigurationById(null)
        refetch()
      }
    } catch (error) {
      enqueueSnackbar('Barang gagal dihapus', {
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('refetch') === 'true') {
      refetch()
      navigate(URL_CONFIGURATIONS)
    }
  }, [location.search])

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, page, rowsPerPage])

  useEffect(() => {
    setPage(1)
  }, [searchInput])

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL ADD CONFIGURATION */}
      <ModalAddConfiguration
        isOpen={isOpenModalAddConfiguration}
        toggle={toggleModalAddConfiguration}
        onSuccess={refetch}
        dataExistingConfiguration={data || []}
        dataConfigurationById={dataConfigurationById}
      />
      {/* END MODAL ADD CONFIGURATION */}

      {/* MODAL REMOVE CONFIGURATION */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal((prev) => !prev)}
        title={`Hapus Barang`}
        dataId={dataConfigurationById?.id}
        desc={`Apakah Anda yakin ingin menghapus barang ${dataConfigurationById?.commodity} dari jenis ${dataConfigurationById?.type}?`}
        isLoading={isRemove}
        onRemoveData={(id) => handleSubmitRemoveById(id)}
        buttonTitle="Hapus"
      />
      {/* END MODAL REMOVE CONFIGURATION */}

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
          breadCrumbActive="Daftar Konfigurasi"
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            columns={columns}
            data={paginatedData}
            progressPending={isFetching}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner color="primary" />
              </div>
            }
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="fw-semibold">Daftar Konfigurasi</h5>

                  <Button
                    // onClick={toggleModalAddConfiguration}
                    onClick={navigateToConfigurationAdd}
                    size="sm"
                    color="primary"
                    data-testid="configurations-add-button"
                  >
                    + Tambah
                  </Button>
                </div>

                <div className="my-4">
                  <PMBSearchInputGroup
                    label={'Cari'}
                    value={searchInput}
                    placeholder="Cari konfigurasi..."
                    onChange={(e) => setSearchInput(e.target.value)}
                    maxWidth={isMobile ? '100%' : '15%'}
                    testId="configurations-search-input"
                  />
                </div>
              </div>
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilter
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada daftar konfigurasi yang tersedia'}
              </p>
            }
          />

          {data?.length > 0 && (
            <PMBPagination
              rowCount={filteredData.length}
              currentPage={page - 1}
              rowsPerPage={rowsPerPage}
              setCurrentPage={(newPage) => setPage(newPage + 1)}
              setRowsPerPage={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage)
                setPage(1)
              }}
              testId="configurations-pagination"
            />
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default ListConfigurations
